import {
  Injectable,
  ApplicationRef
} from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  Observable,
  defer
} from 'rxjs';
import {
  map,
  filter,
  first,
  switchMap,
  distinctUntilChanged,
  share,
  tap
} from 'rxjs/operators';
import { Store } from '@ngrx/store';
import {
  isAppReady,
  isAppStable,
  isAppConfigLoaded,
  isAppInit,
  isAppAuth
} from './life-cycle.selectors';
import { NGXLogger } from 'ngx-logger';

@Injectable({
  providedIn: 'root'
})
export class LifeCycleService {

  public isAppReady$ = this.store$.select(isAppReady).pipe(distinctUntilChanged());

  public isAppStable$ = this.store$.select(isAppStable).pipe(distinctUntilChanged());

  public isAppConfigLoaded$ = this.store$.select(isAppConfigLoaded).pipe(distinctUntilChanged());

  public isAppInit$ = this.store$.select(isAppInit).pipe(distinctUntilChanged());

  public isAppAuthorised$ = this.store$.select(isAppAuth).pipe(distinctUntilChanged());

  public appIsAuthorised$ = this.isAppAuthorised$.pipe(
    filter(Boolean),
    first(),
    share(),
  );

  public appIsStable$ = this.isAppStable$.pipe(
    filter(Boolean),
    first(),
    share(),
  );

  public appIsReady$ = this.isAppReady$.pipe(
    filter(Boolean),
    first(),
    share(),
  );

  public appConfigLoaded$ = this.isAppConfigLoaded$.pipe(
    filter(Boolean),
    first(),
    share(),
  );

  public appInit$ = this.isAppInit$.pipe(
    filter(Boolean),
    first(),
    share(),
  );

  public appIsReady = false;
  private appIsStable = false;
  private appIsAuthorised = false;
  private appConfigLoaded = false;
  private appInit = false;

  public whenReady<T>(observable: Observable<T> | (() => Observable<T>)): Observable<T> {
    if (typeof observable === 'function') {
      observable = defer(observable);
    }

    return this.appIsReady$.pipe(
      switchMap(() => observable)
    );
  }

  public whenStable<T>(observable: Observable<T> | (() => Observable<T>)): Observable<T> {
    if (typeof observable === 'function') {
      observable = defer(observable);
    }

    return this.appIsStable$.pipe(
      switchMap(() => observable)
    );
  }

  public whenAuthorised<T>(observable: Observable<T> | (() => Observable<T>)): Observable<T> {
    if (typeof observable === 'function') {
      observable = defer(observable);
    }

    return this.appIsAuthorised$.pipe(
      switchMap(() => observable)
    );
  }

  /**
   * @deprecated the config is loaded with APP_INITIALIZER and therefor
   * any time present
   * @param observable
   */
  public whenConfigLoaded<T>(observable: Observable<T> | (() => Observable<T>)): Observable<T> {
    if (typeof observable === 'function') {
      observable = defer(observable);
    }

    return this.appConfigLoaded$.pipe(
      switchMap(() => observable)
    );
  }

  public whenAppInit<T>(observable: Observable<T> | (() => Observable<T>)): Observable<T> {
    if (typeof observable === 'function') {
      observable = defer(observable);
    }

    return this.appInit$.pipe(
      switchMap(() => observable)
    );
  }

  constructor(
    public store$: Store<any>,
    private readonly logger: NGXLogger,
  ) {
    this.appIsReady$.pipe(
      tap(value => this.logger.info('[LifeCycleService::appIsReady$]', value)),
    ).subscribe(appIsReady => this.appIsReady = appIsReady);
    this.appIsStable$.pipe(
      tap(value => this.logger.info('[LifeCycleService::appIsStable$]', value)),
    ).subscribe(appIsStable => this.appIsStable = appIsStable);
    this.appIsAuthorised$.pipe(
      tap(value => this.logger.info('[LifeCycleService::appIsAuthorised$]', value)),
    ).subscribe(appIsAuthorised => this.appIsAuthorised = appIsAuthorised);
    this.appConfigLoaded$.pipe(
      tap(value => this.logger.info('[LifeCycleService::appConfigLoaded$]', value)),
    ).subscribe(appConfigLoaded => this.appConfigLoaded = appConfigLoaded);
    this.appInit$.pipe(
      tap(value => this.logger.info('[LifeCycleService::appInit$]', value)),
    ).subscribe(appInit => this.appInit = appInit);
  }

}
